import {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  useMemo,
  Fragment,
} from 'react';
import {
  Button,
  DialogTitle,
  DialogContentText,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useDownloadRouteRoadmapList } from '~hooks/useDownloadRouteRoadmapList';
import { useDownloadRouteRoadmapDetails } from '~hooks/useDownloadRouteRoadmapDetails';
import { Nullable } from '~globals/types';

import {
  DialogExportRouteRoadmapProps,
  DialogExportRouteRoadmapFormat,
} from './types';
import { ITEMS } from './items';
import DialogExportRouteRoadmapListItemButton from './ListItemButton';
import {
  DialogExportRouteRoadmapContainer,
  DialogExportRouteRoadmapContent,
  DialogExportRouteRoadmapActions,
  DialogExportRouteRoadmapListItemIconImage,
} from './styles';

const DialogExportRouteRoadmap = ({
  open,
  routeIds,
  onClose,
}: DialogExportRouteRoadmapProps): ReactElement => {
  const [selectedFormat, setSelectedFormat] =
    useState<Nullable<DialogExportRouteRoadmapFormat>>(null);

  const handleSelectFormat = useCallback(
    (newFormat: DialogExportRouteRoadmapFormat) => {
      setSelectedFormat(newFormat);
    },
    [],
  );

  const isSelectedCurrentFormat = useCallback(
    (currentFormat: DialogExportRouteRoadmapFormat) => {
      if (!selectedFormat) return false;

      return (
        selectedFormat.type === currentFormat.type &&
        selectedFormat.isExcel === currentFormat.isExcel
      );
    },
    [selectedFormat],
  );

  const {
    loading: loadingExportRoadmapList,
    onDownloadRoadmap: onDownloadRoadmapList,
  } = useDownloadRouteRoadmapList({
    onSuccess: onClose,
  });

  const {
    loading: loadingExportRoadmapDetails,
    onDownloadRoadmap: onDownloadRoadmapDetails,
  } = useDownloadRouteRoadmapDetails({
    onSuccess: onClose,
  });

  const loadingExportRoadmap = useMemo(
    () => loadingExportRoadmapList || loadingExportRoadmapDetails,
    [loadingExportRoadmapList, loadingExportRoadmapDetails],
  );

  const handleExportRoadmap = useCallback(async () => {
    if (selectedFormat?.type === 'table') {
      await onDownloadRoadmapList({
        routeIds,
        excelMode: selectedFormat.isExcel,
      });
    }

    if (
      selectedFormat?.type === 'details' ||
      selectedFormat?.type === 'articles'
    ) {
      await onDownloadRoadmapDetails({
        routeIds,
        excelMode: selectedFormat.isExcel,
      });
    }
  }, [
    selectedFormat,
    routeIds,
    onDownloadRoadmapList,
    onDownloadRoadmapDetails,
  ]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSelectedFormat(null);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <DialogExportRouteRoadmapContainer
        open={open}
        onClose={onClose}
        maxWidth="sm"
      >
        <DialogTitle>Exportar documentos de ruta</DialogTitle>

        <DialogExportRouteRoadmapContent dividers>
          <DialogContentText color="text.primary">
            Seleccione el tipo de documento deseado
          </DialogContentText>

          <List component="nav" aria-label="export-route-to-pdf">
            {ITEMS.map((item) => (
              <DialogExportRouteRoadmapListItemButton
                key={`dialog-export-route-map-format-${item.format.type}-${item.format.isExcel}`}
                validSubscriptionFeature={item.validSubscriptionFeature}
                selected={isSelectedCurrentFormat(item.format)}
                onClick={() => handleSelectFormat(item.format)}
              >
                <ListItemIcon>
                  <DialogExportRouteRoadmapListItemIconImage {...item.icon} />
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Fragment>
                      <Typography
                        variant="body2"
                        fontWeight="800"
                        color="primary"
                        component="span"
                      >
                        {item.title}
                      </Typography>
                    </Fragment>
                  }
                  secondary={item.description}
                />
              </DialogExportRouteRoadmapListItemButton>
            ))}
          </List>
        </DialogExportRouteRoadmapContent>
        <DialogExportRouteRoadmapActions>
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          <LoadingButton
            variant="contained"
            color="primary"
            loading={loadingExportRoadmap}
            onClick={handleExportRoadmap}
            disabled={selectedFormat === null}
          >
            Exportar
          </LoadingButton>
        </DialogExportRouteRoadmapActions>
      </DialogExportRouteRoadmapContainer>
    </>
  );
};

export default DialogExportRouteRoadmap;
